import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Select, Space, Spin, Typography, Button, Drawer, Table, Divider, Tag } from 'antd';
import {
    DollarCircleOutlined,
    FileTextOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    FileExcelOutlined
} from '@ant-design/icons';
import {
    FileSyncOutlined
} from '@ant-design/icons';
import { proformaService } from '../../services/proformaService';
import * as XLSX from 'xlsx';

const { Option } = Select;
const { Text } = Typography;

const ProformaSummary = () => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({ count: 0, totalTax: 0, totalAmount: 0, invoices: [] });
    const [period, setPeriod] = useState('year');
    const [value, setValue] = useState(undefined);
    const [isDetailVisible, setIsDetailVisible] = useState(false);

    // Determine default start year for current FY (Apr-Mar cycle)
    const now = new Date();
    const currentFYStartYear = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    const [year, setYear] = useState(currentFYStartYear);

    const fetchStats = async () => {
        try {
            setLoading(true);
            const response = await proformaService.getProformaStats({ period, value, year });
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch proforma stats', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, [period, value, year]);

    const handlePeriodChange = (val) => {
        setPeriod(val);
        setValue(undefined);
    };

    const handleExport = () => {
        if (!stats.invoices || stats.invoices.length === 0) return;

        // Prepare data for XLSX
        const data = stats.invoices.map(inv => ({
            'Proforma Number': inv.invoiceNumber,
            'Customer': inv.customerName,
            'Company': inv.companyName,
            'Date': new Date(inv.invoiceDate).toLocaleDateString(),
            'Amount Before Tax': inv.totalBeforeTax,
            'Tax Amount': inv.totalTax,
            'Total Amount': inv.totalAfterTax
        }));

        // Add summary statistics Row
        const summary = [
            {}, // Empty row
            {
                'Proforma Number': 'SUMMARY',
                'Customer': `Total Proformas: ${stats.count}`,
                'Company': '',
                'Date': '',
                'Amount Before Tax': '',
                'Tax Amount': `Total GST: ₹${stats.totalTax.toFixed(2)}`,
                'Total Amount': `Grand Total: ₹${stats.totalAmount.toFixed(2)}`
            }
        ];

        const worksheet = XLSX.utils.json_to_sheet([...data, ...summary]);

        // Auto-size columns
        const colWidths = [
            { wch: 20 },
            { wch: 30 },
            { wch: 20 },
            { wch: 15 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 }
        ];
        worksheet['!cols'] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Detailed Proformas");

        XLSX.writeFile(workbook, `proforma_invoices_${year}_${period}_${value || 'all'}.xlsx`);
    };

    const months = [
        { label: 'April', value: 3 },
        { label: 'May', value: 4 },
        { label: 'June', value: 5 },
        { label: 'July', value: 6 },
        { label: 'August', value: 7 },
        { label: 'September', value: 8 },
        { label: 'October', value: 9 },
        { label: 'November', value: 10 },
        { label: 'December', value: 11 },
        { label: 'January', value: 0 },
        { label: 'February', value: 1 },
        { label: 'March', value: 2 }
    ];

    const quarters = [
        { label: 'Q1 (Apr-Jun)', value: 1 },
        { label: 'Q2 (Jul-Sep)', value: 2 },
        { label: 'Q3 (Oct-Dec)', value: 3 },
        { label: 'Q4 (Jan-Mar)', value: 4 }
    ];

    const years = [2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030];

    const columns = [
        { title: 'Proforma #', dataIndex: 'invoiceNumber', key: 'invoiceNumber' },
        { title: 'Customer', dataIndex: 'customerName', key: 'customerName' },
        { title: 'Company', dataIndex: 'companyName', key: 'companyName' },
        {
            title: 'Date',
            dataIndex: 'invoiceDate',
            key: 'invoiceDate',
            render: (date) => new Date(date).toLocaleDateString()
        },
        {
            title: 'Before Tax',
            dataIndex: 'totalBeforeTax',
            key: 'totalBeforeTax',
            render: (val) => `₹${val.toFixed(2)}`
        },
        {
            title: 'Tax',
            dataIndex: 'totalTax',
            key: 'totalTax',
            render: (val) => `₹${val.toFixed(2)}`
        },
        {
            title: 'Total',
            dataIndex: 'totalAfterTax',
            key: 'totalAfterTax',
            render: (val) => <Text strong>₹{val.toFixed(2)}</Text>
        },
    ];

    return (
        <div style={{ marginBottom: 24 }}>
            <Card bordered={false} bodyStyle={{ padding: '16px 24px' }} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                        <Space size="middle">
                            <CalendarOutlined style={{ fontSize: 18, color: '#1890ff' }} />
                            <Text strong style={{ fontSize: 16 }}>Financial Year {year}-{String(year + 1).slice(-2)}</Text>
                        </Space>
                        <Space wrap>
                            <Select
                                value={year}
                                onChange={setYear}
                                style={{ width: 100 }}
                                placeholder="Year"
                            >
                                {years.map(y => (
                                    <Option key={y} value={y}>{y}</Option>
                                ))}
                            </Select>

                            <Select
                                value={period}
                                onChange={handlePeriodChange}
                                style={{ width: 120 }}
                                placeholder="Select Period"
                            >
                                <Option value="year">Full Year</Option>
                                <Option value="quarter">Quarterly</Option>
                                <Option value="month">Monthly</Option>
                            </Select>

                            {period === 'month' && (
                                <Select
                                    value={value}
                                    onChange={setValue}
                                    style={{ width: 150 }}
                                    placeholder="Select Month"
                                >
                                    {months.map(m => (
                                        <Option key={m.value} value={m.value}>{m.label}</Option>
                                    ))}
                                </Select>
                            )}

                            {period === 'quarter' && (
                                <Select
                                    value={value}
                                    onChange={setValue}
                                    style={{ width: 150 }}
                                    placeholder="Select Quarter"
                                >
                                    {quarters.map(q => (
                                        <Option key={q.value} value={q.value}>{q.label}</Option>
                                    ))}
                                </Select>
                            )}

                            <Divider type="vertical" />

                            <Button
                                type="primary"
                                ghost
                                icon={<InfoCircleOutlined />}
                                onClick={() => setIsDetailVisible(true)}
                                disabled={stats.count === 0}
                            >
                                View Details
                            </Button>

                            <Button
                                icon={<FileExcelOutlined style={{ color: '#52c41a' }} />}
                                onClick={handleExport}
                                disabled={stats.count === 0}
                            >
                                Export XLSX
                            </Button>
                        </Space>
                    </div>

                    <Spin spinning={loading}>
                        <Row gutter={[20, 20]}>
                            {[
                                { title: 'No. of Proformas', value: stats.count, icon: <FileSyncOutlined />, color: '#1890ff', bg: '#e6f7ff' },
                                { title: 'Total GST', value: stats.totalTax, icon: <DollarCircleOutlined />, color: '#52c41a', bg: '#f6ffed', isCurrency: true },
                                { title: 'Total Amount', value: stats.totalAmount, icon: <DollarCircleOutlined />, color: '#fa8c16', bg: '#fff7e6', isCurrency: true },
                            ].map((item, index) => (
                                <Col xs={24} sm={8} key={index}>
                                    <Card bordered={false} style={{
                                        borderRadius: 16,
                                        background: item.bg,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}>
                                        <div style={{ position: 'absolute', right: -10, top: -10, opacity: 0.1, fontSize: 60, color: item.color }}>
                                            {item.icon}
                                        </div>
                                        <div style={{ position: 'relative', zIndex: 1 }}>
                                            <Text type="secondary" style={{ fontSize: 13, textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5 }}>
                                                {item.title}
                                            </Text>
                                            <div style={{ marginTop: 8, display: 'flex', alignItems: 'baseline', gap: 4 }}>
                                                {item.isCurrency && <span style={{ fontSize: 18, color: item.color, fontWeight: 500 }}>₹</span>}
                                                <Text style={{ fontSize: 28, fontWeight: 700, color: '#262626' }}>
                                                    {item.isCurrency ? item.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : item.value}
                                                </Text>
                                            </div>
                                            <div style={{ marginTop: 4 }}>
                                                <Tag color={item.color} style={{ borderRadius: 10, border: 'none', fontSize: 10 }}>Live Data</Tag>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Spin>

                </Space>
            </Card>

            <Drawer
                title={`Detailed Proformas - FY ${year}-${String(year + 1).slice(-2)}`}
                placement="right"
                width={800}
                onClose={() => setIsDetailVisible(false)}
                open={isDetailVisible}
                extra={
                    <Button
                        type="primary"
                        ghost
                        icon={<FileExcelOutlined />}
                        onClick={handleExport}
                    >
                        Export XLSX
                    </Button>
                }
            >
                <div style={{ marginBottom: 24 }}>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Card size="small" style={{ background: '#f0f5ff', borderLeft: '3px solid #1890ff' }}>
                                <Statistic
                                    title="No. of Invoices"
                                    value={stats.count}
                                    valueStyle={{ fontSize: '18px' }}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card size="small" style={{ background: '#f6ffed', borderLeft: '3px solid #52c41a' }}>
                                <Statistic
                                    title="Total GST"
                                    value={stats.totalTax}
                                    precision={2}
                                    prefix="₹"
                                    valueStyle={{ fontSize: '18px' }}
                                />
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card size="small" style={{ background: '#fff7e6', borderLeft: '3px solid #fa8c16' }}>
                                <Statistic
                                    title="Total Amount"
                                    value={stats.totalAmount}
                                    precision={2}
                                    prefix="₹"
                                    valueStyle={{ fontSize: '18px' }}
                                />
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Table
                    dataSource={stats.invoices}
                    columns={columns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                />
            </Drawer>
        </div>
    );
};

export default ProformaSummary;
